import { useEffect, useMemo, useState } from "react";
import { gitlabApiPrefix } from "../components/SupabaseClient";
import { getGitLabFilePreviewPolicy } from "../config/gitLabFileViewerPolicy";

function decodeGitLabBase64Content(base64Content) {
  const binary = atob(base64Content.replace(/\n/g, ""));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

export const useGitLabFileContent = (
  projectId,
  filePath,
  metadata,
  ref = "HEAD",
  options = {},
) => {
  const { forceOpen = false } = options;
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const previewPolicy = useMemo(() => {
    if (!metadata || !filePath) return null;
    return getGitLabFilePreviewPolicy(filePath, metadata.size);
  }, [filePath, metadata]);

  useEffect(() => {
    if (!projectId || !filePath || !metadata) {
      setContent("");
      setStatus("idle");
      setError(null);
      return;
    }

    const previewDenied = previewPolicy?.previewAllowed !== true;
    const canForceOpen =
      Boolean(previewPolicy?.previewDenialReason) &&
      previewPolicy?.previewAllowed === true;

    if (previewDenied || (canForceOpen && !forceOpen)) {
      setContent("");
      setStatus("blocked");
      setError(null);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const encodedPath = encodeURIComponent(filePath);
    setContent("");
    setStatus("loading");
    setError(null);

    const loadContent = async () => {
      try {
        const fileResp = await fetch(
          `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/files/${encodedPath}?ref=${encodeURIComponent(ref)}`,
          { signal },
        );

        if (!fileResp.ok) {
          throw new Error(
            `GitLab API error: ${fileResp.status} ${fileResp.statusText}`,
          );
        }

        const data = await fileResp.json();
        if (signal.aborted) return;

        setContent(decodeGitLabBase64Content(data.content || ""));
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[useGitLabFileContent] Failed to fetch content:", err);
        setContent("");
        setError(err.message);
        setStatus("error");
      }
    };

    loadContent();

    return () => controller.abort();
  }, [filePath, forceOpen, metadata, previewPolicy, projectId, ref]);

  return {
    content,
    error,
    status,
    warning:
      status === "blocked" ? previewPolicy?.previewDenialReason || null : null,
    canForceOpen:
      status === "blocked" &&
      Boolean(previewPolicy?.previewDenialReason) &&
      previewPolicy?.previewAllowed === true,
  };
};
